import React, { useEffect, useState, useRef } from "react";
import "./number-draw.css";
import { Alert, Flex } from "antd";
import Confetti from "react-confetti";

const LIMIT = 5;

export default function NumberDrawGamePage() {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const generateNumbers = () => {
    const numbers: number[] = [];
    for (let i = 1; i <= LIMIT; i++) {
      numbers.push(i);
    }
    return numbers.sort(() => Math.random() - 0.5);
  };

  const [questions, setQuestions] = useState<number[]>(generateNumbers());
  const [answers, setAnswers] = useState<number[]>(generateNumbers());

  const [selectedQuestion, setSelectedQuestion] = useState<number>();
  const [selectedAnswer, setSelectedAnswer] = useState<number>();

  const handleAnswerClick = (ans: number) => {
    setSelectedAnswer(ans);

    if (ans === selectedQuestion) {
      const qIndex = questions.indexOf(ans);
      const aIndex = answers.indexOf(ans);
      const audio = new Audio("/assets/audio/beep.mp3");
      audio.play();

      setTimeout(() => {
        if (qIndex !== -1 && aIndex !== -1) {
          // Create new arrays instead of mutating in place
          const newQuestions = questions.filter((_, idx) => idx !== qIndex);
          const newAnswers = answers.filter((_, idx) => idx !== aIndex);
          setQuestions(newQuestions);
          setAnswers(newAnswers);
        }
        setSelectedQuestion(undefined);
      }, 500);
    }
  };

  useEffect(() => {
    if (questions.length === 0) {
      triggerConfetti();
    }
  }, [questions]);

  const handleQuestionClick = (num: number) => {
    setSelectedQuestion(num);
  };
  const [showConfetti, setShowConfetti] = useState(false);

  const triggerConfetti = () => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3000); // stops after 3 seconds
  };

  const [connections, setConnections] = useState<Array<{ from: number; to: number }>>([]);
  const [drawing, setDrawing] = useState<{ from: number | null; to: number | null; x: number; y: number } | null>(null);
  const leftRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const rightRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Helper to get center position of a tile relative to SVG
  const getTileCenter = (ref: HTMLDivElement | null) => {
    if (!ref) return { x: 0, y: 0 };
    const rect = ref.getBoundingClientRect();
    const svg = document.getElementById("draw-svg");
    if (!svg) return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    const svgRect = svg.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2 - svgRect.left,
      y: rect.top + rect.height / 2 - svgRect.top,
    };
  };

  // Mouse/touch handlers
  const handleStartDraw = (num: number, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getTileCenter(leftRefs.current[num]);
    setDrawing({ from: num, to: null, x: pos.x, y: pos.y });
  };

  const handleEndDraw = (num: number, e: React.MouseEvent | React.TouchEvent) => {
    if (drawing && drawing.from !== null) {
      setConnections((prev) => [...prev, { from: drawing.from!, to: num }]);
      setDrawing(null);
    }
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (drawing) {
      let clientX = 0, clientY = 0;
      if ('touches' in e && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else if ('clientX' in e) {
        clientX = e.clientX;
        clientY = e.clientY;
      }
      setDrawing({ ...drawing, x: clientX, y: clientY });
    }
  };

  return (
    <div style={{ height: 1200, position: "relative" }}
      onMouseMove={handleMouseMove}
      onTouchMove={handleMouseMove}
    >
      {/* SVG for lines */}
      <svg id="draw-svg" style={{ position: "absolute", top: 0, left: 0, width: "100vw", height: "100vh", pointerEvents: "none", zIndex: 1 }}>
        {connections.map((conn, idx) => {
          const fromPos = getTileCenter(leftRefs.current[conn.from]);
          const toPos = getTileCenter(rightRefs.current[conn.to]);
          return (
            <line
              key={idx}
              x1={fromPos.x}
              y1={fromPos.y}
              x2={toPos.x}
              y2={toPos.y}
              stroke="blue"
              strokeWidth={3}
            />
          );
        })}
        {drawing && drawing.from !== null && (
          (() => {
            const fromPos = getTileCenter(leftRefs.current[drawing.from]);
            return (
              <line
                x1={fromPos.x}
                y1={fromPos.y}
                x2={drawing.x - (document.getElementById("draw-svg")?.getBoundingClientRect().left || 0)}
                y2={drawing.y - (document.getElementById("draw-svg")?.getBoundingClientRect().top || 0)}
                stroke="red"
                strokeWidth={2}
                strokeDasharray="5,5"
              />
            );
          })()
        )}
      </svg>
      <div className="center-align my-40">
        <h3>Number Pairs</h3>
      </div>

      {showConfetti && (
        <Confetti width={windowSize.width} height={windowSize.height} />
      )}

      {questions.length === 0 && (
        <div className="p-40">
          <Alert
            message="Game Complete"
            description="Congratulations"
            type="success"
            showIcon
          />
        </div>
      )}

      <Flex gap={"30%"} className="center-align ">
        <Flex vertical gap={20}>
          {questions.map((q) => (
            <div
              id={`q${q}`}
              key={q}
              className={`tile ${selectedQuestion === q ? "selected" : ""}`}
              ref={el => leftRefs.current[q] = el}
              onMouseDown={e => handleStartDraw(q, e)}
              onTouchStart={e => handleStartDraw(q, e)}
              onClick={() => handleQuestionClick(q)}
            >
              {q}
            </div>
          ))}
        </Flex>

        <Flex vertical gap={20}>
          {answers.map((q) => (
            <div
              key={q}
              className={`tile ${selectedAnswer === q ? "selected" : ""}`}
              ref={el => rightRefs.current[q] = el}
              onMouseUp={e => handleEndDraw(q, e)}
              onTouchEnd={e => { e.preventDefault(); handleEndDraw(q, e); }}
              onClick={() => handleAnswerClick(q)}
            >
              {q}
            </div>
          ))}
        </Flex>
      </Flex>
    </div>
  );
}
